import { useCallback, useState, useMemo } from 'react';
import { Group } from '@visx/group';
import { scaleOrdinal } from 'd3-scale';
import { useTracking } from 'react-tracking';
import debounce from 'lodash/debounce';
import { useTheme } from '@simplywallst/ui-core';
import { toCurrencyTruncated } from '@/utilities/formatting';

import type { CurrencyISO } from '@/constants/currencies';
import type { RawDataType } from './hooks/useSankey';

import Tooltip from '@/components/Tooltip';
import { VisxText } from '@/components/VisxText';

import {
  sankeyLineGenerator,
  normalPath,
  getSankeyMaxDepth,
  getTextAnchor,
  getTextXCoord,
  getTooltipAttachedNode,
  getTooltipAttachedLink,
  calcRectHeight,
} from './utils/utils';

import TitleColor from '../TitleColor';
import SankeyNodeTooltip from './SankeyNodeTooltip';

import { GROUP_TYPES, MAIN_NODES, NODE_NAMES } from './const';

import type { SankeyData } from './types';

import { TooltipContainer } from './styled';

type NodeName = (typeof NODE_NAMES)[keyof typeof NODE_NAMES];

const isNodeName = (name: string): name is NodeName => {
  const names = Object.values(NODE_NAMES);
  return names.includes(name as NodeName);
};

interface Props {
  currencyISO: CurrencyISO;
  sankeyData: SankeyData;
  rawData: RawDataType;
  isMobileScreen: boolean;
  labels: {
    node: (name: NodeName) => string;
  };
}

export const SankeyChart = ({
  currencyISO,
  sankeyData,
  rawData,
  isMobileScreen,
  labels,
}: Props) => {
  const theme = useTheme();
  const { trackEvent } = useTracking({
    modifier: 'fundamentals_sankey_chart',
    subject: 'past',
  });

  const color = scaleOrdinal<{
    segment: string;
    titleBackground: string;
    title: string;
  }>()
    .domain([
      GROUP_TYPES.INCOME,
      GROUP_TYPES.COST_REVENUE,
      GROUP_TYPES.EXPENSES,
      GROUP_TYPES.PROFIT,
      GROUP_TYPES.LOSS,
      GROUP_TYPES.REVENUE_ELIMINATION,
    ])
    .range([
      {
        segment: theme.color.chart01,
        titleBackground: theme.color.chart01,
        title: theme.color.paleWhite,
      },
      {
        segment: theme.color.chart02,
        titleBackground: theme.color.chart02,
        title: theme.color.noir,
      },
      {
        segment: theme.color.chart04,
        titleBackground: theme.color.chart04RGBA(0.4),
        title: theme.color.paleWhite,
      },
      {
        segment: theme.color.good,
        titleBackground: theme.color.good,
        title: theme.color.paleWhite,
      },
      {
        segment: theme.color.bad,
        titleBackground: theme.color.bad,
        title: theme.color.paleWhite,
      },
      {
        segment: theme.color.chart04,
        titleBackground: theme.color.chart04RGBA(0.4),
        title: theme.color.paleWhite,
      },
    ]);

  // Highlighted link indexes
  const [highlightLinkIndexes, setHighlightLinkIndexes] = useState<number[]>(
    []
  );

  const [highlightNodeIndexes, setHighlightNodeIndexes] = useState<number[]>(
    []
  );

  const resetHighlight = useCallback(() => {
    setHighlightLinkIndexes([]);
    setHighlightNodeIndexes([]);
  }, []);

  const debouncedTrackEvent = useMemo(
    () => debounce(trackEvent, 5000, { leading: true }),
    [trackEvent]
  );

  const onLinkHover = useCallback(
    (link) => {
      debouncedTrackEvent({
        action: 'hover',
      });

      setHighlightLinkIndexes([link.index]);
      const highlighNode = getTooltipAttachedNode(link);
      setHighlightNodeIndexes([highlighNode.index]);
    },
    [debouncedTrackEvent]
  );

  const onNodeHover = useCallback(
    (node) => {
      debouncedTrackEvent({
        action: 'hover',
      });

      setHighlightNodeIndexes([node.index]);

      const attachedLink = getTooltipAttachedLink(node);
      setHighlightLinkIndexes(attachedLink ? [attachedLink.index] : []);
    },
    [debouncedTrackEvent]
  );

  const onChartClick = () => {
    trackEvent({
      action: 'click',
    });
  };

  const enableNewPath = true;
  const maxNodeDepth = getSankeyMaxDepth(sankeyData.nodes);

  const getTooltipLabel = (nodeName) =>
    isNodeName(nodeName) ? labels.node(nodeName) : `% of ${nodeName}`;

  return (
    <Group>
      <Group>
        {sankeyData.links.map((link, index) => {
          const path = enableNewPath
            ? sankeyLineGenerator()(link)
            : normalPath(link);
          const isChartHovering =
            highlightLinkIndexes.length > 0 || highlightNodeIndexes.length > 0;
          const isHovering = highlightLinkIndexes.includes(index);
          const isLinkNotHoveringWhileChartIsHovering =
            isChartHovering && !isHovering;

          const opacity = isLinkNotHoveringWhileChartIsHovering ? 0.2 : 1;
          const fillOpacity = 0.25;

          const fill =
            link.groupType === 'Loss'
              ? theme.color.bad
              : color(link.target.groupType).segment;

          const tooltipNode = getTooltipAttachedNode(link);

          const tooltipTitle = (
            <TooltipContainer>
              <SankeyNodeTooltip
                node={tooltipNode}
                currencyISO={currencyISO}
                label={getTooltipLabel}
              />
            </TooltipContainer>
          );

          return enableNewPath ? (
            <Tooltip
              title={tooltipTitle}
              key={`${link.source.name}-${link.target.name}`}
            >
              <path
                d={path ?? ''}
                opacity={opacity}
                fillOpacity={fillOpacity}
                fill={fill}
                onMouseOver={() => onLinkHover(link)}
                onMouseOut={resetHighlight}
                onClick={onChartClick}
              />
            </Tooltip>
          ) : (
            <path
              key={`${link.source.name}-${link.target.name}`}
              d={path ?? ''}
              stroke={
                isHovering
                  ? theme.color.white
                  : link.groupType === 'Loss'
                    ? theme.color.bad
                    : color(link.target.groupType).segment
              }
              strokeWidth={Math.max(1, link.width)}
              opacity={isHovering ? 1 : 0.7}
              fill="none"
              onMouseOver={() => onLinkHover(index)}
              onMouseOut={resetHighlight}
              onClick={onChartClick}
            />
          );
        })}
      </Group>
      {sankeyData.nodes.map((node, index) => {
        const tooltipTitle = (
          <TooltipContainer>
            <SankeyNodeTooltip
              node={node}
              currencyISO={currencyISO}
              label={getTooltipLabel}
            />
          </TooltipContainer>
        );

        const isChartHovering =
          highlightLinkIndexes.length > 0 || highlightNodeIndexes.length > 0;
        const isHovering = highlightNodeIndexes.includes(index);
        const isNodeNotHoveringWhileChartIsHovering =
          isChartHovering && !isHovering;

        const opacity = isNodeNotHoveringWhileChartIsHovering ? 0.2 : 1;

        const textAnchor = getTextAnchor(node, maxNodeDepth);
        const textXCoord = getTextXCoord(node, maxNodeDepth);
        const isEdgeNode = textAnchor === 'start' || textAnchor === 'end';

        const { rectHeight, negRectHeight } = calcRectHeight(
          node,
          rawData?.revenue
        );
        const isTitleColor = MAIN_NODES.includes(node.key);
        const valueTextY =
          isTitleColor || isEdgeNode || isMobileScreen ? -12 : -18;
        return (
          <Group left={node.x0} top={node.y0} key={node.name}>
            <Tooltip title={tooltipTitle}>
              <g>
                <rect
                  id={`rect-${index}`}
                  width={node.x1 - node.x0}
                  height={rectHeight}
                  fill={color(node.groupType).segment}
                  opacity={opacity}
                  strokeWidth={2}
                  onMouseOver={() => onNodeHover(node)}
                  onMouseOut={resetHighlight}
                  onClick={onChartClick}
                />
                {negRectHeight > 1 && (
                  <Group top={rectHeight}>
                    <rect
                      id={`neg-rect-${index}`}
                      width={node.x1 - node.x0}
                      height={negRectHeight}
                      fill={color(GROUP_TYPES.REVENUE_ELIMINATION).segment}
                      opacity={opacity}
                      strokeWidth={2}
                      onMouseOver={() => onNodeHover(node)}
                      onMouseOut={resetHighlight}
                      onClick={onChartClick}
                    />
                  </Group>
                )}
              </g>
            </Tooltip>

            <Tooltip title={tooltipTitle}>
              <g>
                <TitleColor
                  x={textXCoord}
                  groupX={node.x0}
                  bgColor={color(node.groupType).titleBackground}
                  textColor={color(node.groupType).title}
                  textAnchor={textAnchor}
                  isTitleColor={isTitleColor}
                  isEdgeNode={isEdgeNode}
                  isMobileScreen={isMobileScreen}
                >
                  {/* Not all labels come from the predefined set, some are Industries */}
                  {isNodeName(node.name) ? labels.node(node.name) : node.key}
                </TitleColor>
                <VisxText
                  x={textXCoord}
                  y={valueTextY}
                  verticalAnchor="middle"
                  textAnchor={textAnchor}
                  fill={
                    node.groupType === GROUP_TYPES.LOSS
                      ? color(node.groupType).title
                      : theme.color.lightGray
                  }
                  typography={isMobileScreen ? 'tiny' : 'xSmall'}
                >
                  {toCurrencyTruncated(node.value, 2, currencyISO)}
                </VisxText>
              </g>
            </Tooltip>
          </Group>
        );
      })}
    </Group>
  );
};
